import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMyEntries } from '../api/pro';
const ProEntryContext = createContext(null);
const STORAGE_KEY = 'pro_active_entry';
/**
 * Loads the entries a contributor is attached to and tracks the "active" one
 * (persisted per session). All pro pages publish for the active entry; the
 * active id is always kept in sync with a valid attachment (default: first).
 */
export function ProEntryProvider({ children }) {
    const { data: entries = [], isLoading } = useQuery({ queryKey: ['my-entries'], queryFn: getMyEntries });
    const [activeEntryId, setActiveEntryIdState] = useState(() => {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? Number(raw) : null;
    });
    // Keep the active id pointing at a real attachment (default to the first).
    useEffect(() => {
        if (entries.length === 0)
            return;
        const valid = activeEntryId !== null && entries.some((e) => e.id === activeEntryId);
        if (!valid) {
            setActiveEntryIdState(entries[0].id);
            sessionStorage.setItem(STORAGE_KEY, String(entries[0].id));
        }
    }, [entries, activeEntryId]);
    const setActiveEntryId = (id) => {
        setActiveEntryIdState(id);
        sessionStorage.setItem(STORAGE_KEY, String(id));
    };
    const activeEntry = useMemo(() => entries.find((e) => e.id === activeEntryId) ?? null, [entries, activeEntryId]);
    return (_jsx(ProEntryContext.Provider, { value: { entries, activeEntry, activeEntryId, setActiveEntryId, isLoading }, children: children }));
}
export function useProEntry() {
    const ctx = useContext(ProEntryContext);
    if (!ctx) {
        throw new Error('useProEntry must be used within a ProEntryProvider');
    }
    return ctx;
}
